import { IBaseProps } from './data';
import EventEmitter from '../utils/event-emitter';
import { getAttributes, isFunc } from '../utils';
import Component from './Componet';

export default abstract class OY<P extends IBaseProps, S = any> extends Component<P, S> {
  protected eventEmitter = new EventEmitter();

  private propsAttrs: { [i: string]: string } | null = null;

  private setDataTimeout!: number;

  private _nextProps: P | undefined;

  private _nextState: S | undefined;

  abstract $node?: { [i: string]: JQuery };

  /**
   * 更新props。触发渲染
   * @param data
   * @description 注意。使用了@jeact装饰器的类。setProp函数被复写了.用于支持回调函数
   */
  setProp(data: Partial<P>) {
    this._nextProps = { ...this.props, ...this._nextProps, ...data };
    if (this.setDataTimeout) {
      clearTimeout(this.setDataTimeout)
      this.setDataTimeout = 0;
    }
    this.setDataTimeout = setTimeout(() => {
      if (this._nextProps) {
        this.props = this._nextProps;
        this._nextProps = undefined;
      }
      this.render();
    })
  }

  /**
   *  更新state。触发渲染
   * @param data
   * @param callback
   */
  setState(data: Partial<S>, callback?: Function) {
    // eslint-disable-next-line react/no-direct-mutation-state
    this._nextState = { ...this.state, ...this._nextState, ...data } as any;
    if (this.setDataTimeout) {
      clearTimeout(this.setDataTimeout);
      this.setDataTimeout = 0;
    }
    this.setDataTimeout = setTimeout(() => {
      if (this._nextState) {
        // eslint-disable-next-line react/no-direct-mutation-state
        this.state = this._nextState;
        this._nextState = undefined;
      }
      this.render();
      callback && callback();
    })
  }

  /**
   * 监听事件
   * @param name 事件名称
   * @param handler 处理函数
   * @description 事件名称是小写并去掉on前缀
   */
  on(name: string | string[], handler: any) {
    $.each(([] as string[]).concat(name), (idx, ename) => {
      const eventName = 'on' + ename.replace(/^on([a-zA-Z])/, '$1').replace(/^[a-z]/, (ma) => ma.toUpperCase());
      this.eventEmitter.addListener(eventName, handler.bind(this))
    })
  }

  /**
   * 事件绑定。
   * 相比on。此方法可以通过声明式一次绑定多个事件
   * @param eventMap
   */
  addEvent(eventMap: { [i: string]: Function }) {
    $.each(eventMap, (name, handler) => {
      this.eventEmitter.addListener(name, handler.bind(this))
    })
  }

  /**
   * 事件冒泡简短写法
   * @param eventName 事件名称
   * @param args 参数
   */
  protected $emit(eventName: string, ...args: any[]) {
    if (this.listenEvent) {
      this.listenEvent(eventName, ...args)
    }
    return this.eventEmitter.emit(eventName, ...args)
  }

  /**
   * 根据className查找dom节点
   *
   * @param {string} className 类名
   * @param {JQuery} [parent] 父节点。默认为$el
   * @return {JQuery}
   */
  protected $class(className: string, parent?: JQuery) {
    const pNode = parent || this.$el;
    return pNode.find(`.${className}`)
  }

  /**
   * props传递进来的onXXX事件函数进行绑定
   */
  protected bindPropsEvent() {
    Object.keys(this.props).forEach(key => {
      if (/^on[A-Z]/.test(key)) {
        const handler = (this.props as any)[key];
        if (isFunc(handler)) {
          this.eventEmitter.addListener(key, (this.props as any)[key].bind(this))
        }
      }
    })
  }

  /**
   * 获取组件attribute上的props参数。
   * @description 此类函数都是以$符号开头。横杠命名转为驼峰
   */
  protected getAttrProps(parent: JQuery = this.$parent) {
    const attrs = getAttributes(parent.get(0));
    const props: any = {};
    Object.keys(attrs).forEach(key => {
      const value = attrs[key];
      if (/^\$/.test(key)) {
        props[key.slice(1).replace(/-([a-z])/g, (_m, g1) => g1.toUpperCase())] = value;
      }
    })
    return props;
  }

  /**
   * 绑定dom attris
   * @description 将组件上的props参数以及其他参数绑定到根组件上
   */
  protected bindAttris() {
    // WARN: 这里的propsAttrs只能初始化一次.因为对于class等属性的合并没有办法去追踪改变.很遗憾~
    if (!this.propsAttrs) {
      const attrs = getAttributes(this.$parent.get(0));
      this.propsAttrs = { ...attrs, ...this.props.attrs };
    }
    Object.keys(this.propsAttrs).forEach(key => {
      const value = this.propsAttrs![key];
      if (/^\$/.test(key)) {
        return
      }
      if (key === 'class') {
        this.$el.addClass(value)
      } else if (key === 'style') {
        const css = value.split(';').map(va => va.split(':')).reduce((pre, [csskey, cssValue]) => {
          pre[csskey] = cssValue;
          return pre;
        }, {} as any);
        this.$el.css(css);
      } else {
        this.$el.attr(key, value)
      }
    })
  }

  /**
  *
  * @param data 更新state。
  */
  protected _setState(data: Partial<S>) {
    // eslint-disable-next-line react/no-direct-mutation-state
    this.state = { ...this.state, ...data } as any
  }

  protected bindEvent?(): void;

  protected listenEvent?<T = string>(name: T, ...args: any[]): void;

  abstract setNode(): void;
}



// WEBPACK FOOTER //
// ./src/types/OY.ts